import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import transformData from 'modules/_shared/components/Tables/helpers';
import validateOutdatedSocietyValue from 'modules/_shared/api/validationActions';

import { getStockPlans } from 'modules/beneficiaries/redux/planActions';
import { getPartnerList } from 'modules/partners/utils';
import { getHoldingClasses } from 'modules/partners/redux/holdingClassActions';
import { isSocietyConstituted } from 'modules/society/utils/society';
import { getPartnerCategories } from 'modules/partners/redux/partnerCategoryActions';
import { PartnerListCustomData } from 'modules/partners/utils/getPartnerList';

import { State } from 'redux/initialState';
import { useTranslate } from 'hooks/useTranslate';

import menuOptions from '../utils/menuOptions';
import tableColumns from '../utils/tableColumns';

type UsePartnerListProps = {
  isAdmin: boolean;
  isReadOnly: boolean;
  showNationality: boolean;
  page: number;
  items: number;
  sortBy?: string;
  sortOrder?: string;
};

export const usePartnerList = ({
  isAdmin,
  isReadOnly,
  showNationality,
}: UsePartnerListProps) => {
  const { i18n } = useTranslate();
  const dispatch = useDispatch();
  const { societyId, userId } = useParams();

  const user = useSelector((state: State) => state.user);
  const plans = useSelector((state: State) => state?.plans);
  const partners = useSelector((state: State) => state.partners);
  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );
  const hasAccessCaptable = useSelector(
    (state: State) => state.society?.actualSociety?.hasAccessCaptable
  );

  const [tableData, setTableData] = useState([]);
  const [customData, setCustomData] = useState<PartnerListCustomData[]>([]);

  const currentTableColumns = useMemo(
    () => tableColumns(i18n, showNationality),
    [i18n, showNationality]
  );

  const currentMenuOptions = useMemo(() => menuOptions(i18n), [i18n]);

  const filteredMenuOptions = useMemo(() => {
    if (actualSociety?.partnersCount?.total === 0) {
      const [addPartnerOption, constitutionOption] = currentMenuOptions;
      return [addPartnerOption, constitutionOption];
    }

    if (actualSociety?.sharesCount?.actual === 0) {
      const [addPartnerOption, constitutionOption, capitalIncreaseOption] =
        currentMenuOptions;
      return [
        addPartnerOption,
        isSocietyConstituted(actualSociety)
          ? capitalIncreaseOption
          : constitutionOption,
      ];
    }

    return currentMenuOptions.filter((_, index) => index !== 1);
  }, [actualSociety, currentMenuOptions]);

  const mapPartnersToListData = useCallback(async () => {
    if (!actualSociety || actualSociety['_id'] !== societyId) return;

    try {
      const { newData, newCustomData } = await getPartnerList({
        partners,
        actualSociety,
        plans,
        user,
        userId,
        isAdmin,
        isReadOnly,
        hasAccessCaptable,
      });

      setCustomData(newCustomData);

      setTableData(
        newData?.length > 0
          ? transformData({
              data: newData,
              columns: currentTableColumns,
            })
          : []
      );
    } catch (error) {
      setCustomData([]);
      setTableData([]);
    }
  }, [
    actualSociety,
    societyId,
    partners,
    plans,
    user,
    userId,
    isAdmin,
    isReadOnly,
    hasAccessCaptable,
    currentTableColumns,
  ]);

  useEffect(() => {
    if (societyId) {
      dispatch(getStockPlans(societyId));
      dispatch(getHoldingClasses({ societyId }));
      dispatch(getPartnerCategories(societyId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [societyId]);

  useEffect(() => {
    if (!actualSociety) return;

    if (isAdmin) {
      dispatch(validateOutdatedSocietyValue(actualSociety));
    }
  }, [actualSociety, isAdmin]); // eslint-disable-line

  useEffect(() => {
    mapPartnersToListData();
  }, [mapPartnersToListData]);

  return {
    data: {
      filteredMenuOptions,
      tableData,
      customData,
      currentTableColumns,
      showNationality,
    },
  };
};
